import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import type { EVMAddress } from "@opensea/utils"
import { EXPIRATION_IN_MINUTES } from "../../../../lib/zod/expiration"
import type { CreateOfferFlowFormValues, Offer } from "../types"
import { constructOffer_itemFragment } from "./constructOffer.fragment"

export const constructOffer = ({
  item: itemKey,
  formValues,
}: {
  item: FragmentOf<typeof constructOffer_itemFragment>
  formValues: CreateOfferFlowFormValues | undefined
}): Offer | null => {
  if (!formValues) {
    return null
  }

  const item = readFragment(constructOffer_itemFragment, itemKey)
  const { expiration, offers } = formValues
  const firstOffer = offers[0]
  if (!firstOffer) {
    return null
  }

  const { price } = firstOffer
  const contractAddress = item.chain.offerCurrency.address

  return {
    item: {
      tokenId: item.tokenId,
      contractAddress: item.contractAddress as EVMAddress,
      chain: item.chain,
    },
    quantity: firstOffer.quantity,
    startTime: new Date(),
    endTime:
      expiration.expirationType === "custom"
        ? (expiration.customExpiration as Date)
        : new Date(
            Date.now() +
              EXPIRATION_IN_MINUTES[expiration.expirationType] * 60 * 1000,
          ),
    pricePerItem: {
      unit: price,
      contractAddress: contractAddress as EVMAddress,
    },
  }
}

/**
 * Convert items and form values to offers
 * @param items - GQL item fragments
 * @param formValues - Form values from create form
 * @returns Offer[]
 */
export const convertItemsAndFormValuesToOffers = ({
  items: itemKeys,
  formValues,
  startTime,
}: {
  items: FragmentOf<typeof constructOffer_itemFragment>[]
  formValues: CreateOfferFlowFormValues
  startTime: Date
}): Offer[] => {
  const { offers, expiration } = formValues
  const items = itemKeys.map(item =>
    readFragment(constructOffer_itemFragment, item),
  )

  // Instead of mapping over items, map over offers and find matching items
  return offers.flatMap(offer => {
    const item = items.find(item => item.id === offer.id)

    if (!item) {
      // Skip offers that don't have matching items (shouldn't happen in normal flow)
      return []
    }

    const { price } = offer
    const contractAddressTyped = item.chain.offerCurrency.address

    return [
      {
        item: {
          tokenId: item.tokenId,
          contractAddress: item.contractAddress,
          chain: item.chain,
        },
        quantity: offer.quantity,
        startTime,
        endTime:
          expiration.expirationType === "custom"
            ? (expiration.customExpiration as Date)
            : new Date(
                startTime.getTime() +
                  EXPIRATION_IN_MINUTES[expiration.expirationType] * 60 * 1000,
              ),
        pricePerItem: {
          unit: price,
          contractAddress: contractAddressTyped as EVMAddress,
        },
      },
    ]
  })
}
